import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { csvParse, csvFormat } from "d3-dsv";
import Select from "react-select";
import { X, Upload, Eye, Trash2, StopCircle } from "lucide-react";
import ModalPortal from "../ModalPortal";
import CsvPreviewModal from "./CsvPreviewModal";
import blobService from "../../services/blobService";
import campaignService from "../../services/campaignService";
import { decodeAccessToken } from "../../helpers/helpers";
import { singleSelectStyles } from "../../helpers/constant";

const MAX_RECORDS = 1000;
const ACCEPTED_TYPES = ["text/csv"];
const ACTION = "bulk-prescribe";

const normalizePhone = (value) => {
  if (!value) return value;
  return value.replace(/[^0-9]/g, "");
};

const generateUniqueCampaignId = () => {
  const timestamp = Date.now();
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let hash = "";
  for (let i = 0; i < 6; i++) {
    hash += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return `${timestamp}_${hash}`;
};

const formatFileSize = (bytes) => {
  if (!bytes) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const sanitizeCell = (value) =>
  (value || "")
    .replace(/\uFEFF/g, "")
    .replace(/\u00A0/g, " ")
    .replace(/[\u200B-\u200D]/g, "")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/\r/g, "")
    .trim();

const CreateCampaignDrawer = ({ open, onClose }) => {
  // Form fields
  const [name, setName] = useState("");
  const [mode, setMode] = useState("sms");
  const [to, setTo] = useState(null);
  const [message, setMessage] = useState("");

  // CSV state
  const [file, setFile] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRows, setCsvRows] = useState([]);
  const [csvRowsRaw, setCsvRowsRaw] = useState([]);
  const [finalCsv, setFinalCsv] = useState("");
  const [emptyColumns, setEmptyColumns] = useState({});
  const [duplicateColumns, setDuplicateColumns] = useState([]);

  // UI state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewMessage, setPreviewMessage] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Validation errors
  const [csvError, setCsvError] = useState("");
  const [toError, setToError] = useState("");

  const fileInputRef = useRef(null);
  const debounceRef = useRef(null);

  // Recipient options from CSV headers (exclude internal columns)
  const visibleHeaders = csvHeaders.filter((h) => h !== "unique_campaign_id");
  const recipientOptions = visibleHeaders.map((h) => ({ label: h, value: h }));

  // --- CSV Parsing ---
  const parseAndValidateCsv = useCallback((selectedFile) => {
    const reader = new FileReader();

    reader.onload = () => {
      let csvData = reader.result
        .replace(/\r\n/g, "\n")
        .replace(/\r/g, "\n")
        .replace(/[\t\f\v\0]/g, "")
        .trim();

      const parsed = csvParse(csvData);
      const data = parsed.map((row) => {
        const sanitized = {};
        for (const key of parsed.columns) {
          sanitized[key] = sanitizeCell(row[key]);
        }
        return sanitized;
      });
      const headers = [...parsed.columns];

      // Duplicate column check
      const seen = {};
      const dupes = [];
      headers.forEach((h) => {
        if (seen[h]) {
          if (!dupes.includes(h)) dupes.push(h);
        }
        seen[h] = true;
      });

      if (dupes.length) {
        setDuplicateColumns(dupes);
        setCsvError(
          `Duplicate column${dupes.length > 1 ? "s" : ""} detected (${dupes.join(", ")})`
        );
        setCsvHeaders(headers);
        return;
      }

      // Filter empty rows and build data
      const emptyTracker = {};
      const rowData = [];
      const rowsRaw = [];

      for (const row of data) {
        const allEmpty = Object.values(row).every(
          (v) => (v || "").trim() === ""
        );
        if (allEmpty) continue;

        const sanitized = {};
        const rawValues = [];
        for (const key of headers) {
          const val = (row[key] || "").replace(/[\t\f\v\0]/g, "").trim();
          sanitized[key] = val;
          rawValues.push(val);
          if (!val) emptyTracker[key] = true;
        }
        rowData.push(sanitized);
        rowsRaw.push(rawValues);
      }

      // Row count check
      if (rowData.length > MAX_RECORDS) {
        setCsvError(`Please select a file having records up to ${MAX_RECORDS.toLocaleString()}`);
        setCsvHeaders(headers);
        setCsvRows(rowData);
        setCsvRowsRaw(rowsRaw);
        setEmptyColumns(emptyTracker);
        return;
      }

      // Add unique_campaign_id column to each row
      headers.push("unique_campaign_id");
      for (let i = 0; i < rowData.length; i++) {
        const uid = generateUniqueCampaignId();
        rowData[i]["unique_campaign_id"] = uid;
        rowsRaw[i].push(uid);
      }

      // Build final CSV string
      const csvString = csvFormat(rowData, headers);

      setCsvHeaders(headers);
      setCsvRows(rowData);
      setCsvRowsRaw(rowsRaw);
      setEmptyColumns(emptyTracker);
      setDuplicateColumns([]);
      setFinalCsv(csvString);
      setCsvError("");
    };

    reader.readAsText(selectedFile, "utf-8");
  }, []);

  // --- File Selection ---
  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!ACCEPTED_TYPES.includes(selectedFile.type)) {
      toast.error("Please select only .csv files");
      e.target.value = "";
      return;
    }

    setFile(selectedFile);
    setCsvError("");
    setTo(null);
    setToError("");
    setMessage("");
    setPreviewMessage("");
    parseAndValidateCsv(selectedFile);

    // Reset input so same file can be re-selected
    e.target.value = "";
  };

  const deleteFile = () => {
    setFile(null);
    setCsvHeaders([]);
    setCsvRows([]);
    setCsvRowsRaw([]);
    setFinalCsv("");
    setEmptyColumns({});
    setDuplicateColumns([]);
    setCsvError("");
    setTo(null);
    setToError("");
    setMessage("");
    setPreviewMessage("");
  };

  // --- Message Preview ---
  const replaceColumnPlaceholders = useCallback(
    (inputString, headers) => {
      if (!inputString) return "";
      let hasInvalid = false;
      const result = inputString.replace(
        /\{\{column_(\d+)\}\}/g,
        (match, index) => {
          const idx = Number(index);
          if (idx < 1 || idx > headers.length) {
            hasInvalid = true;
            return match;
          }
          return "{{" + headers[idx - 1] + "}}";
        }
      );
      return { result, hasInvalid };
    },
    []
  );

  const replaceVariables = (template, dataObj) => {
    if (!template || !dataObj) return template || "";
    return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, variable) => {
      return dataObj[variable] || "";
    });
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (!message || !csvHeaders.length) {
        setPreviewMessage("");
        return;
      }
      const { result } = replaceColumnPlaceholders(message, csvHeaders);
      const preview = replaceVariables(result, csvRows[0]);
      setPreviewMessage(preview);
    }, 400);

    return () => clearTimeout(debounceRef.current);
  }, [message, csvHeaders, csvRows, replaceColumnPlaceholders]);

  // --- "To" field empty column check ---
  const handleToChange = (option) => {
    setTo(option);
    if (option && emptyColumns[option.value]) {
      setToError("Empty values found in this field");
    } else {
      setToError("");
    }
  };

  // --- Validation ---
  const validate = () => {
    if (!file) return false;
    if (csvError) return false;
    if (!to) return false;
    if (toError) return false;
    if (!mode) return false;
    if (!name.trim()) return false;
    if (!message.trim()) return false;
    return true;
  };

  // --- Submit ---
  const handleSubmit = async () => {
    if (isSubmitting) return;
    setSubmitted(true);

    if (!validate()) return;

    setIsSubmitting(true);
    setIsUploading(true);
    setUploadProgress(0);

    try {
      const user = decodeAccessToken();
      if (!user) {
        toast.error("Session expired. Please login again.");
        return;
      }

      // Build blob name
      const nameParts = file.name.split(".");
      nameParts.pop();
      const blobName = `${user.user_id}/${nameParts.join("") + "_" + Date.now()}.csv`;
      const blobPath = blobName;

      // Replace column placeholders in message
      const { result: finalMsg, hasInvalid } = replaceColumnPlaceholders(
        message,
        csvHeaders
      );

      if (hasInvalid) {
        toast.error("Message contains invalid column references");
        setIsSubmitting(false);
        setIsUploading(false);
        return;
      }

      // Normalize phone numbers in the selected "to" column
      const toColumn = to.value;
      const toColIndex = csvHeaders.indexOf(toColumn);
      const normalizedRows = csvRowsRaw.map((row) => {
        const newRow = [...row];
        if (toColIndex >= 0) {
          newRow[toColIndex] = normalizePhone(newRow[toColIndex]);
        }
        return newRow;
      });
      const normalizedData = normalizedRows.map((row) => {
        const obj = {};
        csvHeaders.forEach((h, i) => { obj[h] = row[i]; });
        return obj;
      });
      const normalizedCsv = csvFormat(normalizedData, csvHeaders);

      // Upload CSV blob to Azure Blob Storage with progress
      const csvBlob = new Blob([normalizedCsv], {
        type: "text/csv;charset=utf-8",
      });

      await blobService.uploadFileWithProgress(
        user.user_id,
        blobName,
        csvBlob,
        (progress) => setUploadProgress(progress)
      );

      setIsUploading(false);
      setUploadProgress(100);

      // Create campaign via API
      const payload = {
        to: to.value,
        mode,
        name: name.trim(),
        message: finalMsg,
        fileName: blobPath,
        owner: String(user.ID),
        action: ACTION,
        facility_id: user.facility_id,
        userName:
          (user.firstname || "") +
          (user.firstname && user.lastname ? " " : "") +
          (user.lastname || ""),
      };

      await campaignService.createCampaign(payload);
      toast.success("Campaign added successfully");
      handleClose(true);
    } catch (err) {
      console.error("Campaign creation error:", err);
      toast.error("Something went wrong! Please try again");
    } finally {
      setIsSubmitting(false);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // --- Drawer Close ---
  const handleClose = (refresh = false) => {
    setName("");
    setMode("");
    setTo(null);
    setMessage("");
    setFile(null);
    setCsvHeaders([]);
    setCsvRows([]);
    setCsvRowsRaw([]);
    setFinalCsv("");
    setEmptyColumns({});
    setDuplicateColumns([]);
    setCsvError("");
    setToError("");
    setPreviewMessage("");
    setSubmitted(false);
    setIsSubmitting(false);
    setIsUploading(false);
    setUploadProgress(0);
    onClose(refresh);
  };

  if (!open) return null;

  const isFormValid = validate();

  return (
    <ModalPortal>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-50 bg-black bg-opacity-40 transition-opacity"
        onClick={() => !isSubmitting && handleClose()}
      />

      {/* Drawer Panel */}
      <div className="fixed top-0 right-0 z-50 h-full w-full max-w-[720px] bg-white shadow-2xl flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-800">
            Create Campaign
          </h2>
          <button
            onClick={() => !isSubmitting && handleClose()}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition text-gray-400 hover:text-gray-600"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="flex flex-col gap-6">
            {/* --- Recipients Section --- */}
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Recipients
            </h3>

            {/* CSV Upload */}
            <div>
              <div className="rounded-xl border border-gray-200 p-5 flex flex-col gap-3 bg-gray-50">
                <div className="flex justify-between items-center">
                  <div className="flex flex-col gap-1">
                    <p className="text-sm font-medium text-gray-700">
                      Add Patients List
                    </p>
                    <p className="text-xs text-gray-400">
                      CSV (max. {MAX_RECORDS.toLocaleString()} records)
                    </p>
                  </div>

                  {!file && (
                    <label className="px-5 py-2 text-sm font-medium border border-gray-300 rounded-full cursor-pointer hover:bg-gray-100 transition bg-white">
                      Browse
                      <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        accept=".csv"
                        onChange={handleFileSelect}
                      />
                    </label>
                  )}
                </div>

                {/* File Card */}
                {file && (
                  <div className="border border-gray-200 rounded-lg p-4 flex items-center gap-3 bg-white">
                    <div className="flex-1 flex flex-col gap-1 min-w-0">
                      <p className="text-sm text-gray-700 truncate">
                        {file.name}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <span>{formatFileSize(file.size)}</span>
                        {isUploading && (
                          <span className="text-[#2B3674] font-medium">
                            {uploadProgress}%
                          </span>
                        )}
                      </div>
                      {!csvError && !isUploading && uploadProgress === 100 && (
                        <p className="text-xs text-green-600 font-medium">
                          File uploaded successfully
                        </p>
                      )}
                      {isUploading && (
                        <div className="w-full h-1 bg-gray-200 rounded-full mt-1">
                          <div
                            className="h-1 bg-[#2B3674] rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                      )}
                    </div>

                    {isUploading ? (
                      <button className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400">
                        <StopCircle size={18} />
                      </button>
                    ) : (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setShowPreview(true)}
                          className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400 hover:text-[#2B3674] transition"
                          title="Preview"
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          onClick={deleteFile}
                          className="p-1.5 rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500 transition"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* CSV Errors */}
              {submitted && !file && !csvError && (
                <p className="text-xs text-red-500 mt-1.5 ml-1">
                  Patient list is required
                </p>
              )}
              {csvError && (
                <p className="text-xs text-red-500 mt-1.5 ml-1">{csvError}</p>
              )}
            </div>

            {/* To (Recipients) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                To (Recipients)
              </label>
              <Select
                value={to}
                onChange={handleToChange}
                options={recipientOptions}
                placeholder="Select recipient column"
                isClearable
                isDisabled={!csvHeaders.length || !!csvError}
                styles={singleSelectStyles}
              />
              {submitted && !to && !toError && (
                <p className="text-xs text-red-500 mt-1.5 ml-1">
                  Recipient column is required
                </p>
              )}
              {toError && (
                <p className="text-xs text-red-500 mt-1.5 ml-1">{toError}</p>
              )}
            </div>

            {/* Divider */}
            <div className="border-b border-gray-200" />

            {/* Mode */}
            {/* <div className="flex flex-col gap-2">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                Mode
              </h3>
              <div className="flex gap-4">
                {["sms", "ivr"].map((m) => (
                  <label
                    key={m}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-xl border cursor-pointer transition text-sm font-medium ${
                      mode === m
                        ? "border-[#2B3674] bg-[#EEF0FB] text-[#2B3674]"
                        : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                    }`}
                  >
                    <input
                      type="radio"
                      name="campaign-mode"
                      value={m}
                      checked={mode === m}
                      onChange={() => setMode(m)}
                      className="accent-[#2B3674]"
                    />
                    {m.toUpperCase()}
                  </label>
                ))}
              </div>
              {submitted && !mode && (
                <p className="text-xs text-red-500 ml-1">Mode is required</p>
              )}
            </div> */}

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Campaign name"
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/35 focus:border-blue-500 transition"
              />
              {submitted && !name.trim() && (
                <p className="text-xs text-red-500 mt-1.5 ml-1">
                  Name is required
                </p>
              )}
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Message
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your message. Use {{column_1}}, {{column_2}}, etc. to insert dynamic fields."
                rows={6}
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/35 focus:border-blue-500 transition"
              />
              {submitted && !message.trim() && (
                <p className="text-xs text-red-500 mt-1.5 ml-1">
                  Message is required
                </p>
              )}

              {/* Column references helper */}
              {visibleHeaders.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {visibleHeaders.map((h, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-xs text-gray-500 cursor-default"
                      title={`Use {{column_${i + 1}}} in your message`}
                    >
                      <span className="font-mono text-[#2B3674]">
                        {"{{column_" + (i + 1) + "}}"}
                      </span>
                      <span>= {h}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Message Preview */}
            {!csvError &&
              file &&
              message.trim() &&
              previewMessage && (
                <div className="rounded-xl bg-[#EEF0FB]/60 p-4 text-sm flex flex-col gap-1 min-h-[120px]">
                  <h4 className="font-semibold text-gray-700 text-xs uppercase tracking-wide mb-1">
                    Message Preview
                  </h4>
                  <p className="text-gray-600 whitespace-pre-line leading-relaxed">
                    {previewMessage}
                  </p>
                </div>
              )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={() => !isSubmitting && handleClose()}
            disabled={isSubmitting}
            className="px-5 py-2.5 text-sm font-medium rounded-full border border-gray-200 hover:bg-gray-50 transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || (submitted && !isFormValid)}
            className="px-6 py-2.5 text-sm font-medium rounded-full bg-[#2B3674] text-white hover:bg-[#232d61] transition disabled:opacity-50 flex items-center gap-2"
          >
            {isSubmitting && (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            )}
            {isSubmitting ? "Launching..." : "Launch Campaign"}
          </button>
        </div>
      </div>

      {/* CSV Preview Modal */}
      <CsvPreviewModal
        open={showPreview}
        onClose={() => setShowPreview(false)}
        headers={csvHeaders}
        rows={csvRows}
      />
    </ModalPortal>
  );
};

export default CreateCampaignDrawer;
